<template>
	<header-block :title="document ? document.documentName : 'Document'" :del="!editMode" @delete="deleteDocument" :edit="!editMode" @edit="onEdit" menuDescription="Update how this document is named, described or filed." menuEditLabel="Edit details" menuDeleteLabel="Delete document"></header-block>
	<transition name="toast-fade">
		<div v-if="showToast" class="doc-toast">
			<img :src="require('@/assets/check-tick.svg')" class="doc-toast-icon" />
			{{ toastMessage }}
		</div>
	</transition>
	<CustomerLoadWait :customer="document" />
	<div v-if="document" class="documentPage">
		<AiChatBanner />

		<div class="tabBar">
			<button :class="['tab', { active: activeTab === 'summary' }]" @click="activeTab = 'summary'">Summary</button>
			<button :class="['tab', { active: activeTab === 'about' }]" @click="activeTab = 'about'">About</button>
		</div>

		<!-- Summary Tab -->
		<div v-if="activeTab === 'summary'" class="tabContent">
			<!-- AI Overview -->
			<div v-if="document.aiSummary" class="ai-overview">
				<div class="ai-overview-header">
					<img :src="require('@/assets/aisummary.png')" class="ai-marker" />
					<span class="ai-overview-title">AI Overview</span>
				</div>
				<p class="ai-overview-text">{{ maskedAiSummary }}</p>
			</div>

			<div class="detailRows" v-if="wellData.length > 0">
				<div class="detailRow" v-for="item in wellData" :key="item.order">
					<span class="detailLabel">{{ item.label }}</span>
					<span class="detailValue" v-if="!editMode">{{ _processMonetaryAmounts(item.value) }}</span>
					<input v-else class="detailInput" v-model="item.value" />
				</div>
			</div>
			<div v-if="editMode" class="editActions">
				<v-row class="button-row">
					<v-col class="button-col">
						<MGButton @click="onSave" text="Save" :stacked="true" />
						<MGButton @click="editMode = false" :outline="true" :stacked="true" text="Cancel" />
					</v-col>
				</v-row>
			</div>
			<div v-if="document.updateDateTimeString" class="lastUpdated">
				Last analysed: {{ document.updateDateTimeString }}
			</div>
		</div>

		<!-- About Tab -->
		<div v-if="activeTab === 'about'" class="tabContent">

			<!-- Categorise this document -->
			<div class="section">
				<div class="sectionTitle">Categorise this document</div>
				<FieldDrawer
					v-model="selectedCategory"
					label="Category"
					:items="categoryOptions"
					:required="true"
					:clearable="false"
					:disabled="!editMode"
				/>
				<div class="subCategoryRow">
					<div class="subCategoryConnector">
						<div class="connectorV"></div>
						<div class="connectorH"></div>
					</div>
					<div class="subCategoryField">
						<FieldDrawer
							v-model="selectedSubCategory"
							label="Sub-category"
							:items="subCategoryOptions"
							:required="true"
							:clearable="false"
							:showAvatars="false"
							:disabled="!editMode"
						/>
					</div>
				</div>
			</div>

			<!-- Provider -->
			<div v-if="selectedProvider" class="detailRow">
				<span class="detailLabel">Provider</span>
				<span class="detailValue">{{ properCase(selectedProvider) }}</span>
			</div>

			<div class="section">
				<div class="sectionTitle">Details</div>
				<InputSections v-if="editMode"
					:sections="aboutEditSections"
					theme="light"
				/>
				<div v-else class="detailRows">
					<div class="detailRow">
						<span class="detailLabel">Subject</span>
						<span class="detailValue">{{ document.documentSubject }}</span>
					</div>
					<div class="detailRow">
						<span class="detailLabel">Description</span>
						<span class="detailValue">{{ document.documentName }}</span>
					</div>
					<div class="detailRow">
						<span class="detailLabel">File name</span>
						<span class="detailValue">{{ document.name }}</span>
					</div>
				</div>
				<div class="detailRow">
					<span class="detailLabel">Added on</span>
					<span class="detailValue">{{ document.createDateTimeString }}</span>
				</div>
			</div>

			<div v-if="editMode" class="editActions">
				<v-row class="button-row">
					<v-col class="button-col">
						<MGButton @click="onSaveAbout" :fullWidth="true" text="Save" :stacked="true" />
						<MGButton @click="onCancelAbout" :outline="true" :fullWidth="true" :stacked="true" text="Cancel" />
					</v-col>
				</v-row>
			</div>

			<div class="section">
				<div class="previewHeader">
					<div class="sectionTitle">Preview</div>
					<router-link :to="'/Documents/' + documentId + '/view'" class="viewDocLink">View document</router-link>
				</div>
				<div class="previewCard" @click="$router.push('/Documents/' + documentId + '/view')">
					<div v-if="!previewLoaded || pdfError" class="pdf-preview-placeholder">
						<v-icon class="pdf-icon">mdi-file-document-outline</v-icon>
						<span class="pdf-tap-text">Tap to view</span>
					</div>
					<template v-if="document.isPdf">
						<VuePdfEmbed v-if="!pdfError" :source="document.url" :page="1" :class="['pdf-preview', { 'preview-hidden': !previewLoaded }]" @rendered="previewLoaded = true" @rendering-failed="pdfError = true" @loading-failed="pdfError = true" />
					</template>
					<img v-else :src="document.url" :alt="document.name" :class="['image-preview', { 'preview-hidden': !previewLoaded }]" @load="previewLoaded = true" />
				</div>
			</div>
		</div>
	</div>

</template>

<script>
import Helpers from '../mixins/Helpers';
import Services from '../mixins/Services';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import FieldDrawer from './elements/FieldDrawer.vue';
import InputSections from './elements/InputSections.vue';
import AiChatBanner from './elements/AiChatBanner.vue';
import VuePdfEmbed from 'vue-pdf-embed';

export default {
	mixins: [Services, Helpers],
	components: {CustomerLoadWait, HeaderBlock, MGButton, FieldDrawer, InputSections, VuePdfEmbed, AiChatBanner},
	data() {
		return {
			document: null,
			wellData: [],
			editMode: false,
			activeTab: 'summary',
			selectedCategory: '',
			selectedSubCategory: '',
			selectedProvider: '',
			documentTypes: [],
			categoryOptions: [],
			pdfError: false,
			previewLoaded: false,
			showToast: false,
			toastMessage: '',
			loaded: false,
			aboutEditSections: [],
		}
	},
	computed: {
		docCategory() {
			if (!this.document?.sysoDocumentType?.path) return '';
			const parts = this.document.sysoDocumentType.path.split(' > ');
			return parts[1] || '';
		},
		docSubCategory() {
			if (!this.document?.sysoDocumentType?.path) return '';
			const parts = this.document.sysoDocumentType.path.split(' > ');
			return parts[2] || '';
		},
		maskedAiSummary() {
			if (!this.document?.aiSummary) return '';
			return this._processMonetaryAmounts(this.document.aiSummary);
		},
		subCategoryOptions() {
			if (!this.selectedCategory || !this.documentTypes.length) return [];
			const subs = this.documentTypes
				.filter(t => t.path.split(' > ')[1] === this.selectedCategory && t.path.split(' > ')[2])
				.map(t => t.path.split(' > ')[2]);
			return [...new Set(subs)];
		},
		categoryImage() {
			return this._getDocumentCategoryImage(this.selectedCategory);
		},
	},
	methods: {
		onLoad: async function() {
			this.documentId = this.$route.params.id;
			this._getCustomer();
			this._getDocumentTypes((types) => {
				this.documentTypes = types;
				const uniqueCategories = [...new Set(types.map(t => t.path.split(' > ')[1]).filter(Boolean))];
				this.categoryOptions = uniqueCategories.map(name => ({
					text: name,
					value: name,
					logo: this._getDocumentCategoryImage(name)
				}));
			});
			this._getDocument(this.documentId, (doc) => {
				this.document = doc;
				this.selectedCategory = this.docCategory;
				this.selectedSubCategory = this.docSubCategory;
				this.selectedProvider = doc.company || '';
				this.$nextTick(() => { this.loaded = true; });
				this.wellData = doc.wellData.DataArray.map((item, index) => {
					return {
						order: index + 1,
						label: item.Title,
						value: item.Value
					};
				});
			});
		},

		deleteDocument(done) {
			this._deleteDocument(this.documentId, () => {
				this._clearDocuments();
				if (done) done();
				this.$router.back();
			});
		},

		onEdit() {
			this.aboutEditSections = [{
				fields: [
					{ id: 'subject', type: 'text', label: 'Subject', value: this.document.documentSubject || '', required: false },
					{ id: 'description', type: 'text', label: 'Description', value: this.document.documentName || '', required: false },
					{ id: 'filename', type: 'text', label: 'File name', value: this.document.name || '', required: false },
				]
			}];
			this.activeTab = 'about';
			this.editMode = true;
		},

		properCase(str) {
			if (!str) return '';
			return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
		},

		onSaveAbout() {
			const fields = this.aboutEditSections[0]?.fields || [];
			fields.forEach(f => {
				if (f.id === 'subject') this.document.documentSubject = f.value;
				if (f.id === 'description') this.document.documentName = f.value;
				if (f.id === 'filename') this.document.name = f.value;
			});
			const payload = JSON.parse(JSON.stringify(this.document));
			this._updateDocument(payload, () => {
				this.editMode = false;
				this.toastMessage = 'Document updated';
				this.showToast = true;
				setTimeout(() => { this.showToast = false; }, 3000);
			});
		},

		onCancelAbout() {
			this.editMode = false;
			this.selectedCategory = this.docCategory;
			this.selectedSubCategory = this.docSubCategory;
			this._getDocument(this.documentId, (doc) => {
				this.document = doc;
			});
		},

		onSave() {
			this.document.wellData.DataArray = this.wellData.map(item => ({
				Title: item.label,
				Value: item.value
			}));
			this.document.processedMeta = JSON.stringify(this.document.wellData);
			this._updateDocument(this.document, () => {
				this.editMode = false;
			});
		}
	},
	watch: {
		selectedCategory(_, oldVal) {
			if (oldVal) this.selectedSubCategory = '';
		},
		selectedSubCategory(val) {
			if (val && this.loaded) {
				const matchingType = this.documentTypes.find(t => {
					const parts = t.path.split(' > ');
					return parts[1] === this.selectedCategory && parts[2] === val;
				});
				if (matchingType) {
					this.document.sysoDocumentType = { ...matchingType };
				}
			}
		}
	},
	mounted: function () {
		this.onLoad();
	},
}
</script>

<style scoped>
.documentPage {
	padding: 0 0px;
}

.tabBar {
	display: flex;
	gap: 12px;
	padding-bottom: 10px;
}

.tab {
	flex: 1;
	padding: 12px 24px 14px;
	border: none;
	border-radius: 6px;
	font-size: 13px;
	text-align: center;
	background: white;
	color: var(--brand-purple);
}

.tab.active {
	background: var(--brand-purple);
	color: white;
}

.tabContent {
	padding: 20px 0 32px;
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.section {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.sectionTitle {
	font-size: 15px;
	font-weight: bold;
	color: var(--brand-purple);
}

.detailRows {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.detailRow {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 16px;
	background: rgba(255, 255, 255, 0.9);
	border-radius: 8px;
}

.detailLabel {
	font-size: 13px;
	line-height: 16px;
	color: var(--brand-purple);
}

.detailValue {
	font-size: 13px;
	line-height: 16px;
	font-weight: bold;
	color: var(--brand-purple);
	word-break: break-word;
}

.detailRow--editing {
	background: #3328470D;
	border-radius: 8px;
	position: relative;
}

.detailRow--editing .detailLabel {
	font-size: 11px;
	line-height: 12px;
	opacity: 0.5;
}

.detailInput {
	flex: 1;
	font-size: 13px;
	line-height: 16px;
	color: var(--brand-purple);
	text-align: left;
	border: none;
	border-radius: 0;
	padding: 0;
	background: transparent;
	outline: none;
}

.editActions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
}

.previewHeader {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.viewDocLink {
	font-size: 13px;
	font-weight: bold;
	color: var(--brand-purple);
	text-decoration: none;
}

.previewCard {
	display: block;
	background: white;
	border-radius: 8px;
	padding: 24px 16px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
	text-decoration: none;
}

.pdf-preview {
	max-height: 324px;
	overflow: hidden;
	border-radius: 8px;
}

.image-preview {
	max-height: 324px;
	width: 100%;
	object-fit: contain;
	border-radius: 8px;
}

.preview-hidden {
	height: 0;
	overflow: hidden;
}

.pdf-preview-placeholder {
	height: 200px;
	border-radius: 8px;
	background: var(--background-color-alt);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
}

.pdf-icon {
	font-size: 48px;
	color: var(--brand-purple);
	opacity: 0.4;
}

.pdf-tap-text {
	font-size: 13px;
	color: var(--brand-purple);
	opacity: 0.5;
}

.previewFileName {
	font-size: 11px;
	color: var(--brand-purple);
	margin-top: 12px;
	text-align: center;
}

.lastUpdated {
	font-size: 13px;
	color: var(--brand-purple);
	text-align: center;
}

.subCategoryRow {
	display: flex;
	align-items: flex-start;
	gap: 16px;
}

.subCategoryConnector {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	flex-shrink: 0;
}

.connectorV {
	width: 1px;
	height: 27px;
	background: rgba(65, 54, 85, 0.1);
}

.connectorH {
	width: 32px;
	height: 1px;
	background: rgba(65, 54, 85, 0.1);
}

.subCategoryField {
	flex: 1;
}

.subCategoryField .field-drawer-trigger {
	margin-bottom: 0;
}

/* AI Overview */
.ai-overview {
	background: white;
	border-radius: 8px;
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.ai-overview-header {
	display: flex;
	align-items: center;
	gap: 8px;
}

.ai-marker {
	width: 16px;
	height: 16px;
}

.ai-overview-title {
	font-size: 16px;
	font-weight: bold;
	color: var(--brand-purple);
}

.ai-overview-text {
	font-size: 15px;
	line-height: 22px;
	color: var(--brand-purple);
	margin: 0;
}

.section :deep(.field-drawer-trigger) {
	min-height: 56px;
	height: 56px;
	padding: 10px 16px;
	box-sizing: border-box;
	margin-bottom: 0;
}

.doc-toast {
	position: fixed;
	top: 98px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 100;
	width: 335px;
	background: white;
	color: var(--text-color);
	padding: 8px 16px;
	border-radius: 8px;
	font-size: 14px;
	text-align: center;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	box-shadow: 0px 4px 12px rgba(51, 40, 71, 0.12);
}

.doc-toast-icon {
	width: 16px;
	height: 16px;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
	transition: opacity 0.3s;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
	opacity: 0;
}
</style>
